// Gemeinsame Typen und Rechenhilfen des Teilsystems Budget (Server und Web).
// Alle Beträge sind ganze Zahlen in Hundertsteln der Währung („Cent“) – nie Gleitkomma-Summen.
// Die Projektwährung ist fest Euro (Marc). Belege dürfen in jeder Währung erfasst werden und werden
// zum Kurs am Belegdatum in Euro umgerechnet; der Hauptbetrag ist überall der Euro-Betrag.
export const PROJEKT_WAEHRUNG = 'EUR';
export const BELEG_ARTEN = ['rechnung', 'angebot', 'einnahme'];
/** Rechnung: offen/bezahlt. Angebot: offen/umgewandelt (in eine Rechnung)/abgelehnt. Einnahme: offen (erwartet)/erhalten. */
export const RECHNUNG_STATUS = ['offen', 'bezahlt'];
export const ANGEBOT_STATUS = ['offen', 'umgewandelt', 'abgelehnt'];
export const EINNAHME_STATUS = ['offen', 'erhalten'];
export const STATUS_NAMEN = {
    offen: 'offen',
    bezahlt: 'bezahlt',
    umgewandelt: 'in Rechnung umgewandelt',
    abgelehnt: 'abgelehnt',
    erhalten: 'erhalten',
};
/** Statusname passend zur Art („offen“ heißt bei Einnahmen „erwartet“). */
export function statusName(art, status) {
    if (art === 'einnahme' && status === 'offen')
        return 'erwartet';
    return STATUS_NAMEN[status];
}
export function statusListe(art) {
    return art === 'rechnung' ? RECHNUNG_STATUS : art === 'angebot' ? ANGEBOT_STATUS : EINNAHME_STATUS;
}
/**
 * Einnahmen bekommen frei eintragbare Labels (Marc: „Kategorien als Label“), mehrere je Einnahme.
 * Diese Vorschläge erscheinen, solange sie im Projekt noch nicht benutzt sind.
 */
export const LABEL_VORSCHLAEGE = ['Zuschuss', 'Sponsoring', 'Verkauf', 'Spende', 'Beitrag'];
export const MAX_LABELS = 10;
export const MAX_LABEL_LAENGE = 40;
/**
 * Labels bereinigen: trimmen, Leerraum zusammenfassen, Länge begrenzen, Doppelte (ohne Groß/klein) entfernen.
 * `bekannt`: schon benutzte Labels – deren Schreibweise wird übernommen („sponsoring“ → „Sponsoring“).
 */
export function labelsBereinigen(roh, bekannt = []) {
    const nachKlein = new Map(bekannt.map((l) => [l.toLocaleLowerCase('de'), l]));
    const aus = [];
    const gesehen = new Set();
    for (const r of roh) {
        const t = r.replace(/\s+/g, ' ').trim().slice(0, MAX_LABEL_LAENGE).trim();
        if (!t)
            continue;
        const k = t.toLocaleLowerCase('de');
        if (gesehen.has(k))
            continue;
        gesehen.add(k);
        aus.push(nachKlein.get(k) ?? t);
        if (aus.length >= MAX_LABELS)
            break;
    }
    return aus;
}
/** Übliche Sätze in Deutschland zur Auswahl (andere lassen sich frei eintragen). */
export const STEUER_SAETZE = [1900, 700, 0];
export const KURS_QUELLE_NAMEN = {
    gleich: 'gleiche Währung',
    ezb: 'EZB-Kurs',
    ersatz: 'letzter bekannter Kurs (Kursquelle war nicht erreichbar)',
    manuell: 'von Hand eingetragen',
    fehlt: 'kein Kurs bekannt',
};
/** Währungen mit EZB-Referenzkurs (frankfurter) plus Namen für die Auswahl. */
export const WAEHRUNGEN = {
    EUR: 'Euro',
    USD: 'US-Dollar',
    GBP: 'Britisches Pfund',
    CHF: 'Schweizer Franken',
    JPY: 'Japanischer Yen',
    CNY: 'Chinesischer Yuan',
    PLN: 'Polnischer Złoty',
    CZK: 'Tschechische Krone',
    HUF: 'Ungarischer Forint',
    SEK: 'Schwedische Krone',
    NOK: 'Norwegische Krone',
    DKK: 'Dänische Krone',
    RON: 'Rumänischer Leu',
    BGN: 'Bulgarischer Lew',
    TRY: 'Türkische Lira',
    ISK: 'Isländische Krone',
    AUD: 'Australischer Dollar',
    CAD: 'Kanadischer Dollar',
    NZD: 'Neuseeland-Dollar',
    HKD: 'Hongkong-Dollar',
    SGD: 'Singapur-Dollar',
    KRW: 'Südkoreanischer Won',
    INR: 'Indische Rupie',
    IDR: 'Indonesische Rupiah',
    MYR: 'Malaysischer Ringgit',
    PHP: 'Philippinischer Peso',
    THB: 'Thailändischer Baht',
    ILS: 'Israelischer Schekel',
    BRL: 'Brasilianischer Real',
    MXN: 'Mexikanischer Peso',
    ZAR: 'Südafrikanischer Rand',
};
// ------------------------------------------------------------------ Rechenhilfen
const KURS_STELLEN = 10;
const KURS_FAKTOR = 10n ** BigInt(KURS_STELLEN);
/** Dezimaltext → ganze Zahl mit `stellen` Nachkommastellen (exakt, kaufmännisch gerundet). */
export function dezimalSkaliert(text, stellen) {
    const t = text.trim();
    const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(t);
    if (!m || (!m[2] && !m[3]))
        return null;
    const vorz = m[1] ? -1n : 1n;
    const ganz = m[2] || '0';
    const nach = m[3] ?? '';
    const nachVoll = (nach + '0'.repeat(stellen + 1)).slice(0, stellen + 1);
    let wert = BigInt(ganz) * 10n ** BigInt(stellen + 1) + BigInt(nachVoll || '0');
    // eine Stelle mehr gelesen → kaufmännisch runden
    wert = (wert + 5n) / 10n;
    return vorz * wert;
}
/** Ganze Zahl mit `stellen` Nachkommastellen → Dezimaltext ohne überflüssige Nullen. */
export function dezimalText(wert, stellen) {
    const neg = wert < 0n;
    const a = neg ? -wert : wert;
    const f = 10n ** BigInt(stellen);
    const ganz = a / f;
    let nach = (a % f).toString().padStart(stellen, '0').replace(/0+$/, '');
    return `${neg ? '-' : ''}${ganz}${nach ? `.${nach}` : ''}`;
}
/** Kurs normalisieren (z. B. "1,0823" → "1.0823"), null bei ungültigem oder nicht positivem Kurs. */
export function kursNormal(text) {
    const s = dezimalSkaliert(text.replace(',', '.'), KURS_STELLEN);
    if (s === null || s <= 0n)
        return null;
    return dezimalText(s, KURS_STELLEN);
}
/** Betrag umrechnen: cent × kurs, exakt mit ganzen Zahlen, kaufmännisch auf Cent gerundet. */
export function umrechnen(cent, kurs) {
    const k = dezimalSkaliert(kurs, KURS_STELLEN);
    if (k === null)
        throw new Error('ungültiger Kurs');
    const p = BigInt(cent) * k;
    const halb = KURS_FAKTOR / 2n;
    const r = p >= 0n ? (p + halb) / KURS_FAKTOR : -((-p + halb) / KURS_FAKTOR);
    return Number(r);
}
/** Kreuzkurs aus EZB-Kursen (Einheiten je 1 EUR): 1 Einheit „von“ = x Einheiten „nach“. */
export function kreuzkurs(jeEuroVon, jeEuroNach) {
    const v = dezimalSkaliert(jeEuroVon, KURS_STELLEN);
    const n = dezimalSkaliert(jeEuroNach, KURS_STELLEN);
    if (!v || !n || v <= 0n || n <= 0n)
        throw new Error('ungültiger Kurs');
    // n / v mit 10 Nachkommastellen, kaufmännisch gerundet
    const r = (n * KURS_FAKTOR * 2n + v) / (v * 2n);
    return dezimalText(r, KURS_STELLEN);
}
/** Kurs zwischen zwei Währungen aus einer Kurstabelle (je 1 EUR), null wenn eine fehlt. */
export function kursAusTabelle(jeEuro, von, nach) {
    if (von === nach)
        return '1';
    const v = von === 'EUR' ? '1' : jeEuro[von];
    const n = nach === 'EUR' ? '1' : jeEuro[nach];
    if (!v || !n)
        return null;
    return kreuzkurs(v, n);
}
/**
 * Eingabe wie „1.234,56“, „1234.5“, „12,–“ oder „-30“ → Cent. null = ungültig.
 * Ein einzelner Punkt mit genau drei Ziffern dahinter gilt (deutsch) als Tausenderpunkt.
 */
export function centAusText(eingabe) {
    let t = eingabe.replace(/[\s €$£¥]|[A-Z]{3}/g, '').replace(/,[-–]$/, '');
    if (!t)
        return null;
    const punkte = (t.match(/\./g) ?? []).length;
    const kommas = (t.match(/,/g) ?? []).length;
    if (punkte && kommas) {
        const dezimal = t.lastIndexOf(',') > t.lastIndexOf('.') ? ',' : '.';
        const tausend = dezimal === ',' ? '.' : ',';
        t = t.split(tausend).join('').replace(dezimal, '.');
    }
    else if (kommas) {
        if (kommas > 1)
            t = t.split(',').join('');
        else
            t = t.replace(',', '.');
    }
    else if (punkte > 1 || /^-?\d{1,3}\.\d{3}$/.test(t)) {
        t = t.split('.').join('');
    }
    const s = dezimalSkaliert(t, 2);
    if (s === null)
        return null;
    const n = Number(s);
    return Number.isSafeInteger(n) ? n : null;
}
/** Zahl (z. B. aus einer KI- oder Mail-Auswertung) → Cent, exakt über den Dezimaltext. */
export function centAusZahl(n) {
    if (!Number.isFinite(n))
        return null;
    const s = dezimalSkaliert(n.toFixed(6), 2);
    if (s === null)
        return null;
    const c = Number(s);
    return Number.isSafeInteger(c) ? c : null;
}
/** Cent → Eingabetext „1234,56“ (für Formulare). */
export function centAlsEingabe(cent) {
    const neg = cent < 0;
    const a = BigInt(Math.abs(cent));
    return `${neg ? '-' : ''}${a / 100n},${(a % 100n).toString().padStart(2, '0')}`;
}
/** Ganzzahlige Division, kaufmännisch gerundet (Hälfte vom Nullpunkt weg). */
function geteilt(z, n) {
    if (n === 0n)
        throw new Error('Division durch null');
    const neg = z < 0n !== n < 0n;
    const a = z < 0n ? -z : z;
    const b = n < 0n ? -n : n;
    const r = (a * 2n + b) / (b * 2n);
    return neg ? -r : r;
}
function ganz(b) {
    const n = Number(b);
    if (!Number.isSafeInteger(n))
        throw new Error('Betrag zu groß');
    return n;
}
/**
 * Mehrwertsteuer-Posten aus zwei bekannten Werten ergänzen (exakt, kaufmännisch gerundet):
 * netto + Steuer, brutto + Steuer, netto + Satz, brutto + Satz, netto + brutto oder Satz + Steuer.
 * null = zu wenig Angaben.
 */
export function steuerErgaenzen(t) {
    const da = (v) => typeof v === 'number' && Number.isSafeInteger(v);
    const satzAus = (netto, steuer) => netto === 0 ? 0 : Math.max(0, Math.min(10000, ganz(geteilt(BigInt(steuer) * 10000n, BigInt(netto)))));
    const { satzBp: s, nettoCent: n, steuerCent: st, bruttoCent: b } = t;
    if (da(n) && da(st))
        return { satzBp: da(s) ? s : satzAus(n, st), nettoCent: n, steuerCent: st };
    if (da(b) && da(st))
        return { satzBp: da(s) ? s : satzAus(b - st, st), nettoCent: b - st, steuerCent: st };
    if (da(n) && da(s))
        return { satzBp: s, nettoCent: n, steuerCent: ganz(geteilt(BigInt(n) * BigInt(s), 10000n)) };
    if (da(b) && da(s)) {
        const netto = ganz(geteilt(BigInt(b) * 10000n, 10000n + BigInt(s)));
        return { satzBp: s, nettoCent: netto, steuerCent: b - netto };
    }
    if (da(n) && da(b))
        return { satzBp: satzAus(n, b - n), nettoCent: n, steuerCent: b - n };
    if (da(s) && da(st) && s > 0)
        return { satzBp: s, nettoCent: ganz(geteilt(BigInt(st) * 10000n, BigInt(s))), steuerCent: st };
    return null;
}
/** Summe brutto aller Posten (= Belegbetrag). */
export function steuerBrutto(posten) {
    let s = 0n;
    for (const p of posten)
        s += BigInt(p.nettoCent) + BigInt(p.steuerCent);
    return ganz(s);
}
/**
 * Posten in Euro: Steuer je Posten umgerechnet, brutto so verteilt, dass die Summe genau den
 * umgerechneten Belegbetrag ergibt (der letzte Posten bekommt den Rundungsrest).
 */
export function postenInEuro(posten, kurs, gesamtEuroCent) {
    let rest = gesamtEuroCent;
    return posten.map((p, i) => {
        const steuer = umrechnen(p.steuerCent, kurs);
        const brutto = i === posten.length - 1 ? rest : umrechnen(p.nettoCent + p.steuerCent, kurs);
        rest -= brutto;
        return { satzBp: p.satzBp, nettoCent: brutto - steuer, steuerCent: steuer, bruttoCent: brutto };
    });
}
/** Satz für die Anzeige: 1900 → „19 %“, 750 → „7,5 %“. */
export function satzText(bp) {
    if (bp === null)
        return 'ohne Steuerangabe';
    const g = Math.trunc(bp / 100);
    const n = String(Math.abs(bp % 100)).padStart(2, '0').replace(/0+$/, '');
    return `${g}${n ? `,${n}` : ''} %`;
}
/** „19“, „7,5“ oder „19 %“ → Hundertstel Prozent; null = ungültig. */
export function satzAusText(t) {
    const s = dezimalSkaliert(t.replace('%', '').trim().replace(',', '.'), 2);
    if (s === null || s < 0n || s > 10000n)
        return null;
    return Number(s);
}
const formate = new Map();
/** Cent → „1.234,56 €“. Nur Anzeige über Intl (gerechnet wird nie hier). */
export function betrag(cent, waehrung) {
    if (cent === null || cent === undefined)
        return '–';
    let e = formate.get(waehrung);
    if (!e) {
        try {
            e = {
                f: new Intl.NumberFormat('de-DE', { style: 'currency', currency: waehrung, minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                mitCode: false,
            };
        }
        catch {
            e = { f: new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), mitCode: true };
        }
        formate.set(waehrung, e);
    }
    const text = e.f.format(cent / 100);
    return e.mitCode ? `${text} ${waehrung}` : text;
}
/** Kurs für die Anzeige: „1 USD = 0,9234 EUR“ (mindestens 4, höchstens 6 Nachkommastellen). */
export function kursText(kurs, von, nach) {
    const [g = '0', n = ''] = kurs.split('.');
    const ohne = n.replace(/0+$/, '');
    const stellen = Math.min(6, Math.max(4, ohne.length));
    return `1 ${von} = ${g},${n.padEnd(stellen, '0').slice(0, stellen)} ${nach}`;
}
/** Heutiges Datum als JJJJ-MM-TT (Ortszeit). */
export function heute() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
export function monatName(jjjjmm) {
    const [j, m] = jjjjmm.split('-');
    const i = Number(m) - 1;
    return MONATE[i] ? `${MONATE[i]} ${j}` : jjjjmm;
}
//# sourceMappingURL=budget.js.map